import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Card, 
  CardContent, 
  Grid,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { financeApi } from '../../services/api/finance.api';

export const DocumentsApiTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Record<string, any>>({});
  const [error, setError] = useState<string | null>(null);

  const testEndpoint = async (name: string, testFn: () => Promise<any>) => {
    setLoading(true);
    setError(null);
    try {
      const result = await testFn();
      setResults(prev => ({ ...prev, [name]: { success: true, data: result } }));
    } catch (err: any) {
      setResults(prev => ({ 
        ...prev, 
        [name]: { 
          success: false, 
          error: err.message || 'Неизвестная ошибка' 
        } 
      }));
      setError(`${name}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const testContracts = () => testEndpoint('Contracts List', async () => {
    return financeApi.contracts.getContracts({ page: 1, limit: 10 });
  });

  const testContractsSummary = () => testEndpoint('Contracts Summary', async () => {
    return financeApi.contracts.getContractsSummary();
  });

  const testInvoices = () => testEndpoint('Invoices List', async () => {
    return financeApi.invoices.getInvoices({ page: 1, limit: 10 });
  });

  const testInvoicesSummary = () => testEndpoint('Invoices Summary', async () => {
    return financeApi.invoices.getInvoicesSummary();
  });

  const testReports = () => testEndpoint('Reports List', async () => {
    return financeApi.documents.getReports({ page: 1, limit: 10 });
  });

  const testReportsSummary = () => testEndpoint('Reports Summary', async () => {
    return financeApi.documents.getReportsSummary();
  });

  const testAllEndpoints = async () => {
    await testContracts();
    await testContractsSummary();
    await testInvoices();
    await testInvoicesSummary();
    await testReports();
    await testReportsSummary();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        🧪 Тестирование Documents API
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Тестирование 21 endpoint из Documents модуля
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item>
          <Button 
            variant="contained" 
            onClick={testAllEndpoints}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Тестировать все endpoints
          </Button>
        </Grid>
        <Grid item>
          <Button 
            variant="outlined" 
            onClick={testContracts}
            disabled={loading}
          >
            Тест Contracts
          </Button>
        </Grid>
        <Grid item>
          <Button 
            variant="outlined" 
            onClick={testInvoices}
            disabled={loading}
          >
            Тест Invoices
          </Button>
        </Grid>
        <Grid item>
          <Button 
            variant="outlined" 
            onClick={testReports}
            disabled={loading}
          >
            Тест Reports
          </Button>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        {Object.entries(results).map(([name, result]) => (
          <Grid item xs={12} md={6} key={name}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {name}
                </Typography>
                {result.success ? (
                  <Alert severity="success">
                    ✅ Успешно
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {JSON.stringify(result.data, null, 2)}
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="error">
                    ❌ Ошибка: {result.error}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {Object.keys(results).length === 0 && (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              Нажмите кнопку для начала тестирования
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default DocumentsApiTest;
